import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Tag,
  Rocket,
  CheckCircle2,
  User,
  Clock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  PppcActivityAction,
  PppcActivityEntry,
  getPppcActivityForApp,
  getPppcActivityForDevice,
} from "@/services/pppcActivityLog";
import { PppcTaggedDiff } from "./PppcTaggedDiff";
import { formatInTimezone, formatRelativeTime } from "@/lib/timezonePreference";
import { useTimezonePreference } from "@/hooks/useTimezonePreference";

/**
 * Cross-cutting timeline of PPPC lifecycle events. The same component is
 * reused on the Application detail page (filtered by appId/bundleId) and
 * on the Device detail page (filtered by deviceId), so a single source of
 * truth in localStorage powers both views.
 *
 * Subscribes to the `pppc-activity-updated` window event so freshly-recorded
 * entries appear without a page reload.
 */

type Scope =
  | { kind: "app"; appId?: string; bundleId?: string }
  | { kind: "device"; deviceId: string };

interface PppcActivityTimelineProps {
  scope: Scope;
  /** Cap how many entries render in the scrollable list. */
  limit?: number;
  /** Compact spacing for use inside dense panels (e.g. device tabs). */
  compact?: boolean;
}

const ACTION_META: Record<
  PppcActivityAction,
  { label: string; icon: typeof Plus; tone: string }
> = {
  created: { label: "Created", icon: Plus, tone: "bg-success-muted text-success" },
  updated: { label: "Updated", icon: Pencil, tone: "bg-primary/10 text-primary" },
  deleted: { label: "Deleted", icon: Trash2, tone: "bg-destructive/10 text-destructive" },
  tagged: { label: "Tags changed", icon: Tag, tone: "bg-accent text-accent-foreground" },
  deployed: { label: "Deployed", icon: Rocket, tone: "bg-primary/10 text-primary" },
  installed: {
    label: "Installed on device",
    icon: CheckCircle2,
    tone: "bg-success-muted text-success",
  },
};

// Timestamp helpers live in `@/lib/timezonePreference` so the timeline,
// the policy detail activity log, and the global Logs page render in
// the same admin-selected zone. The hook below keeps the component
// re-rendering when the preference changes elsewhere.

function readEntries(scope: Scope): PppcActivityEntry[] {
  return scope.kind === "device"
    ? getPppcActivityForDevice(scope.deviceId)
    : getPppcActivityForApp({ appId: scope.appId, bundleId: scope.bundleId });
}

export function PppcActivityTimeline({
  scope,
  limit = 50,
  compact = false,
}: PppcActivityTimelineProps) {
  const [entries, setEntries] = useState<PppcActivityEntry[]>(() => readEntries(scope));
  const tz = useTimezonePreference();

  // Re-read whenever the scope changes…
  useEffect(() => {
    setEntries(readEntries(scope));
  }, [
    scope.kind,
    (scope as any).appId,
    (scope as any).bundleId,
    (scope as any).deviceId,
  ]);

  // …and whenever any PPPC activity is written elsewhere in the app.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => setEntries(readEntries(scope));
    window.addEventListener("pppc-activity-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("pppc-activity-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [scope]);

  const visible = useMemo(() => entries.slice(0, limit), [entries, limit]);

  if (visible.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        No PPPC activity recorded yet for this {scope.kind}.
      </p>
    );
  }

  return (
    <ScrollArea className={compact ? "h-[260px]" : "h-[320px]"}>
      <ul className={compact ? "space-y-2 pr-2" : "space-y-3 pr-2"}>
        {visible.map((e) => {
          const meta = ACTION_META[e.action] ?? ACTION_META.updated;
          const Icon = meta.icon;
          return (
            <li
              key={e.id}
              className="flex items-start gap-3 rounded-md border bg-muted/30 p-3"
            >
              <span
                className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full ${meta.tone}`}
                aria-hidden="true"
              >
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {meta.label}
                  </Badge>
                  <span
                    className="text-xs text-muted-foreground inline-flex items-center gap-1"
                    title={formatInTimezone(e.at, tz)}
                  >
                    <Clock className="h-3 w-3" />
                    {formatRelativeTime(e.at)}
                  </span>
                  {e.actor && (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {e.actor}
                    </span>
                  )}
                </div>
                {e.summary && (
                  <p className="mt-1 text-sm text-foreground break-words">
                    {e.summary}
                  </p>
                )}
                {e.action === "tagged" && (
                  <PppcTaggedDiff
                    previousTagIds={e.previousTagIds}
                    tagIds={e.tagIds}
                    compact={compact}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </ScrollArea>
  );
}

export default PppcActivityTimeline;
